/**
 * Pure state helpers for the Boards page, which lists from Postgres (through
 * api2) and binds each row on screen to its Firestore board.
 *
 * Postgres decides which boards exist, in what order, and what a search or a
 * filter matches; each row then binds `runnits/{id}` and stays live. Everything
 * the page decides from those two sources lives here, so it can be specified
 * without mounting a `.vue` file: the request builder (table state -> endpoint
 * request), the row state (loading, live, removed), the stand-in a row renders
 * until its board arrives, and the CSV, which is built from the Postgres rows
 * alone.
 */
import moment from 'moment-timezone';
import { DbTimestamp, Runnit, RUNNIT_TYPE, RUNNITS_ACCESS_LEVEL } from '@run-diffusion/shared';
import {
	BoardRow,
	BoardsFetchResult,
	BoardsOrder,
	BoardsRequest,
	BoardsSort,
	BoardsView,
	defaultBoardsOrder,
} from '@/utils/boardsApi';
import { BoardsFilter } from '@/views/Runnits/pages/RunnitBoardsPage/types';
import { CursorWalk, restartWalk } from '@/utils/cursorWalk';
import { PanelFilters, countPanelFilters, normalizePanelFilters } from '@/utils/panelFilters';

/** The fields of Vuetify's `options.sync` the endpoint can act on. */
export interface BoardsTableOptions {
	itemsPerPage?: number;
	sortBy?: string[];
	sortDesc?: boolean[];
}

/**
 * Header `value`s of the boards table. Sorting is by header value, and
 * `boardsSortForOptions` is the only place one meets an endpoint sort name.
 */
export const BOARDS_HEADER_VALUE = {
	TITLE: 'title',
	DESCRIPTION: 'description',
	CREATED: 'createdAt',
	OWNER: 'userId',
	ACCESS: 'accessLevel',
	ACTIONS: 'actions',
} as const;

/** A custom field column's header value is this plus the field's key. */
export const BOARDS_CUSTOM_FIELD_HEADER_PREFIX = 'customFields.';

export const BOARDS_ITEMS_PER_PAGE_OPTIONS: number[] = [25, 100];

export const BOARDS_DEFAULT_OPTIONS: BoardsTableOptions = {
	itemsPerPage: BOARDS_ITEMS_PER_PAGE_OPTIONS[0],
	sortBy: [BOARDS_HEADER_VALUE.CREATED],
	sortDesc: [true], // newest first
};

/**
 * The Boards Filters box, as every list table has one (see panelFilters.ts):
 * which boards (the view), one access level, and whose. View always holds a
 * value and rests at the Default view; the other two rest empty. A personal account
 * has only its own private boards, so it has no box.
 */
export interface BoardsPanelFilters extends PanelFilters {
	view: string;
	accessLevel: string | null;
	ownerUserIds: string[];
}

export const BOARDS_RESTING_FILTERS: BoardsPanelFilters = {
	view: BoardsFilter.ALL,
	accessLevel: null,
	ownerUserIds: [],
};

export interface BoardsViewItem {
	text: string;
	value: string;
	// The line under the title saying which boards the view lists
	description: string;
	icon: string;
}

/**
 * The box's View field: the same four ways to look at a team's boards the app has always offered,
 * each a title with a line saying what it lists (the wording and icons of the menu it replaced).
 * `teamLabel` is the company's word for a team (displayLabels.team).
 */
export function boardsViewItems(canSeeEveryonesBoards: boolean, teamLabel: string): BoardsViewItem[] {
	return [
		{
			text: 'Default view',
			value: BoardsFilter.ALL,
			description: 'Your private boards and all boards shared with you',
			// The Boards icon, as the side nav shows it
			icon: 'mdi-view-dashboard-outline',
		},
		{
			text: 'My boards',
			value: BoardsFilter.MY_BOARDS,
			description: 'Only boards you are the owner of',
			icon: 'mdi-account-star-outline',
		},
		{
			text: 'Shared with me',
			value: BoardsFilter.SHARED_WITH_ME,
			description: 'Your shared boards and other boards shared with you',
			icon: 'mdi-account-multiple-outline',
		},
		...(canSeeEveryonesBoards ? [{
			text: 'Everyone\'s boards',
			value: BoardsFilter.EVERYONES_BOARDS,
			description: `(Admin) All boards across the ${teamLabel}`,
			icon: 'mdi-shield-account',
		}] : []),
	];
}

export const BOARDS_ACCESS_LEVEL_ITEMS: { text: string; value: string }[] = [
	{ text: 'Private', value: RUNNITS_ACCESS_LEVEL.PRIVATE },
	{ text: 'Shared', value: RUNNITS_ACCESS_LEVEL.SHARED },
	{ text: 'Restricted', value: RUNNITS_ACCESS_LEVEL.RESTRICTED },
];

/** The box's View as the endpoint's `view`. A personal account has only its own boards. */
export function boardsViewForFilter(filter: BoardsFilter | string | null | undefined, teamMode: boolean): BoardsView {
	if (!teamMode) return 'all';
	switch (filter) {
		case BoardsFilter.MY_BOARDS:
			return 'mine';
		case BoardsFilter.SHARED_WITH_ME:
			return 'shared_with_me';
		case BoardsFilter.EVERYONES_BOARDS:
			return 'everyone';
		default:
			return 'all';
	}
}

export interface BoardsSortSpec {
	sort: BoardsSort;
	order: BoardsOrder;
	customFieldKey: string | null;
}

/**
 * The endpoint sort for the table's sort header. Anything the endpoint cannot
 * sort by (no header, or one that is not sortable) is the default: newest first.
 */
export function boardsSortForOptions(options: BoardsTableOptions | null | undefined): BoardsSortSpec {
	const header: string | undefined = options && Array.isArray(options.sortBy) ? options.sortBy[0] : undefined;
	const desc: boolean = !!(options && Array.isArray(options.sortDesc) && options.sortDesc[0]);
	const order: BoardsOrder = desc ? 'desc' : 'asc';
	if (header === BOARDS_HEADER_VALUE.TITLE) return { sort: 'title', order, customFieldKey: null };
	if (header === BOARDS_HEADER_VALUE.DESCRIPTION) return { sort: 'description', order, customFieldKey: null };
	if (header === BOARDS_HEADER_VALUE.CREATED) return { sort: 'created', order, customFieldKey: null };
	if (header && header.startsWith(BOARDS_CUSTOM_FIELD_HEADER_PREFIX) && header.length > BOARDS_CUSTOM_FIELD_HEADER_PREFIX.length) {
		return { sort: 'custom_field', order, customFieldKey: header.slice(BOARDS_CUSTOM_FIELD_HEADER_PREFIX.length) };
	}
	return { sort: 'created', order: defaultBoardsOrder('created'), customFieldKey: null };
}

/** Whether a header value is one the endpoint can sort by. */
export function isSortableBoardsHeader(header: string | null | undefined, customFieldKeys: string[]): boolean {
	if (!header) return false;
	if (header.startsWith(BOARDS_CUSTOM_FIELD_HEADER_PREFIX)) {
		return customFieldKeys.includes(header.slice(BOARDS_CUSTOM_FIELD_HEADER_PREFIX.length));
	}
	return ([BOARDS_HEADER_VALUE.TITLE, BOARDS_HEADER_VALUE.DESCRIPTION, BOARDS_HEADER_VALUE.CREATED] as string[]).includes(header);
}

export interface BuildBoardsRequestInput {
	// The selected team, or null in the personal workspace.
	teamId: string | null;
	cursor: string | null;
	options: BoardsTableOptions | null | undefined;
	searchQuery: string | null;
	// The applied Filters box. Ignored in the personal workspace, which has none.
	filters: BoardsPanelFilters;
	itemsPerPage: number;
}

/**
 * Table state to endpoint request. It never asks for members: the table's
 * Members cell reads them live, and the CSV has no members column.
 */
export function buildBoardsRequest(input: BuildBoardsRequestInput): BoardsRequest {
	const teamMode: boolean = !!input.teamId;
	const { sort, order, customFieldKey } = boardsSortForOptions(input.options);
	const filters: BoardsPanelFilters = normalizePanelFilters(BOARDS_RESTING_FILTERS, input.filters);
	return {
		teamId: input.teamId || null,
		view: boardsViewForFilter(filters.view, teamMode),
		cursor: input.cursor || null,
		limit: input.itemsPerPage,
		sort,
		order,
		customFieldKey,
		q: (input.searchQuery || '').trim() || null,
		ownerUserIds: teamMode ? filters.ownerUserIds : [],
		accessLevel: teamMode ? filters.accessLevel : null,
	};
}

/**
 * Everything a cursor depends on, as one string: the account, the view, the
 * sort, the search and the owners. A remembered position is restored only when
 * this matches, because the endpoint refuses a cursor in any other listing.
 */
export function boardsListingKey(request: BoardsRequest): string {
	return JSON.stringify([
		request.teamId || null,
		request.view || null,
		request.sort || null,
		request.order || null,
		request.customFieldKey || null,
		request.q || null,
		[...(request.ownerUserIds || [])].sort(),
		request.accessLevel || null,
	]);
}

export interface RememberedBoardsView {
	cursor: string | null;
	page: number;
	itemsPerPage: number;
	sortBy: string[];
	sortDesc: boolean[];
	// The listing the cursor belongs to, and the search and box that produced it.
	filters: Record<string, string | string[] | null>;
}

/** A page number from anywhere: a whole number of at least 1, or 1. */
function wholePageNumber(value: unknown): number {
	const page: number = Math.floor(Number(value));
	return Number.isFinite(page) && page >= 1 ? page : 1;
}

/**
 * What the page stores on the way out: its sort, its page size, its search and
 * Filters box, and where the walk had got to.
 */
export function rememberBoardsView(
	options: BoardsTableOptions | null | undefined,
	listingKey: string,
	cursor: string | null,
	page: number,
	searchQuery?: string | null,
	panelFilters?: BoardsPanelFilters | null,
): RememberedBoardsView {
	const o: BoardsTableOptions = options || {};
	return {
		cursor: cursor || null,
		page: cursor ? wholePageNumber(page) : 1,
		itemsPerPage: Number(o.itemsPerPage) || BOARDS_ITEMS_PER_PAGE_OPTIONS[0],
		sortBy: Array.isArray(o.sortBy) ? [...o.sortBy] : [],
		sortDesc: Array.isArray(o.sortDesc) ? [...o.sortDesc] : [],
		filters: {
			...normalizePanelFilters(BOARDS_RESTING_FILTERS, panelFilters),
			searchQuery: searchQuery || '',
			listing: listingKey,
		},
	};
}

export interface StoredBoardsView {
	cursor?: string | null;
	page?: number | null;
	itemsPerPage?: number;
	sortBy?: string[];
	sortDesc?: boolean[];
	filters?: Record<string, unknown> | null;
}

/**
 * The search and Filters box to restore, or null when none were stored (a view
 * an older page left, which kept them elsewhere). The box is restored with
 * every field cleaned, since the store keeps whatever was put there.
 */
export function hydrateBoardsFilters(
	stored: StoredBoardsView | null | undefined,
): { searchQuery: string; filters: BoardsPanelFilters } | null {
	const storedFilters: Record<string, unknown> | null = stored && stored.filters && typeof stored.filters === 'object'
		? stored.filters
		: null;
	if (!storedFilters || typeof storedFilters.searchQuery !== 'string') return null;
	return {
		searchQuery: storedFilters.searchQuery,
		filters: normalizePanelFilters(BOARDS_RESTING_FILTERS, storedFilters as Partial<Record<keyof BoardsPanelFilters, unknown>>),
	};
}

/**
 * The options to restore from what was stored, on top of the defaults. The
 * store keeps whatever an older version of the page put there, so nothing is
 * trusted: the page size must be one the table offers, and the sort must name
 * a column the endpoint can sort by.
 */
export function hydrateBoardsOptions(
	stored: StoredBoardsView | null | undefined,
	defaults: BoardsTableOptions,
	itemsPerPageOptions: number[],
	customFieldKeys: string[],
): BoardsTableOptions {
	const options: BoardsTableOptions = { ...defaults };
	if (!stored) return options;
	const itemsPerPage: number = Math.floor(Number(stored.itemsPerPage));
	if (itemsPerPageOptions.includes(itemsPerPage)) options.itemsPerPage = itemsPerPage;
	const sortHeader: string | undefined = Array.isArray(stored.sortBy) ? stored.sortBy[0] : undefined;
	if (isSortableBoardsHeader(sortHeader, customFieldKeys)) {
		options.sortBy = [sortHeader];
		options.sortDesc = [Array.isArray(stored.sortDesc) && stored.sortDesc[0] === true];
	}
	return options;
}

/**
 * The walk to resume: the stored position, only when it was taken in the very
 * listing about to be fetched. Its stack is not stored, so Back from a restored
 * page starts the walk over.
 */
export function hydrateBoardsWalk(stored: StoredBoardsView | null | undefined, listingKey: string): CursorWalk {
	const cursor: string | null = stored && typeof stored.cursor === 'string' && stored.cursor ? stored.cursor : null;
	const storedListing: unknown = stored && stored.filters ? stored.filters.listing : null;
	const storedKey: string | null = typeof storedListing === 'string' ? storedListing : null;
	if (!cursor || storedKey !== listingKey) return restartWalk();
	return { ...restartWalk(), cursor, startPage: wholePageNumber(stored.page) };
}

/**
 * Where a row's own binding has got to. `static` is a binding that failed for
 * a reason other than the board being gone: the row keeps showing what
 * Postgres said, without the actions that need the live board.
 */
export type BoardBindState = 'loading' | 'bound' | 'failed';
export type BoardLiveState = 'loading' | 'live' | 'removed' | 'static';

/**
 * What a row shows. A board is removed once its live document says it was
 * deleted, or is gone altogether, and the mirror catches up on the next fetch.
 * An edit never removes one: a board that no longer fits the list's filter or
 * sort stays where it is, live, until the list is fetched again.
 */
export function boardLiveState(
	live: Partial<Runnit> | null | undefined,
	bindState: BoardBindState,
): BoardLiveState {
	if (bindState === 'loading') return 'loading';
	if (bindState === 'failed') return 'static';
	if (!live) return 'removed';
	if (live.deletedAt) return 'removed';
	return 'live';
}

/**
 * What a row renders until its live board arrives: the same fields, read from
 * the Postgres row, in the board's own shape so the cells need not know the
 * difference. `toTimestamp` turns the ISO creation time into the Firestore
 * timestamp the Created cell reads; it is passed in so this file never imports
 * Firebase.
 */
export function stubRunnitFromRow(row: BoardRow, toTimestamp: (iso: string) => DbTimestamp): Runnit {
	const customFields: Record<string, string> = {};
	(row.customFields || []).forEach((field) => {
		if (field.value !== null && field.value !== undefined) customFields[field.key] = field.value;
	});
	return {
		id: row.id,
		title: row.title,
		description: row.description,
		// A personal board has no level of its own; the endpoint reports it as PRIVATE.
		accessLevel: row.teamId ? row.accessLevel : null,
		userId: row.ownerUserId,
		teamId: row.teamId,
		type: RUNNIT_TYPE.WORKFLOW,
		customFields,
		createdAt: row.created ? toTimestamp(row.created) : null,
		avatarId: null,
		avatar: null,
		isFeatured: false,
		deletedAt: null,
	} as unknown as Runnit;
}

export interface BoardDownloadHeader {
	text: string;
	value: string;
}

/**
 * The CSV's columns: the table's, plus the ones a table cell shows as chips
 * (the owner and the access level; not the members). A team listing's custom fields
 * come from the rows themselves, which carry every field the company defines
 * with its label, so the CSV needs nothing but the Postgres rows.
 */
export function boardDownloadHeaders(rows: BoardRow[], teamMode: boolean): BoardDownloadHeader[] {
	const customFieldHeaders: BoardDownloadHeader[] = [];
	if (teamMode) {
		const seen: Record<string, true> = {};
		(rows || []).forEach((row: BoardRow) => {
			(row.customFields || []).forEach((field) => {
				if (seen[field.key]) return;
				seen[field.key] = true;
				customFieldHeaders.push({
					text: field.label || field.key,
					value: `${BOARDS_CUSTOM_FIELD_HEADER_PREFIX}${field.key}`,
				});
			});
		});
	}
	return [
		{ text: 'Name', value: 'title' },
		{ text: 'Description', value: 'description' },
		...customFieldHeaders,
		{ text: 'Created', value: 'created' },
		...(teamMode ? [
			{ text: 'Owner', value: 'owner' },
			{ text: 'Access', value: 'access' },
		] : []),
	];
}

export interface BoardDownloadRow {
	id: string;
	title: string;
	description: string;
	customFields: Record<string, string>;
	created: string;
	owner: string;
	access: string;
}

const ACCESS_LEVEL_LABEL: Record<string, string> = {
	[RUNNITS_ACCESS_LEVEL.PRIVATE]: 'Private',
	[RUNNITS_ACCESS_LEVEL.SHARED]: 'Shared',
	[RUNNITS_ACCESS_LEVEL.RESTRICTED]: 'Restricted',
};

/** A selected board in the CSV: one readable value per column, keyed by header value. */
export function boardDownloadRow(row: BoardRow): BoardDownloadRow {
	const customFields: Record<string, string> = {};
	(row.customFields || []).forEach((field) => {
		customFields[field.key] = field.value || '';
	});
	return {
		id: row.id,
		title: row.title || '',
		description: row.description || '',
		customFields,
		created: row.created ? moment(row.created).format('YYYY-MM-DD') : '',
		owner: row.ownerEmail || '',
		access: ACCESS_LEVEL_LABEL[row.accessLevel] || '',
	};
}

/** The CSV sorts its rows like the table, by the matching column of the download row. */
export function boardsDownloadSort(options: BoardsTableOptions | null | undefined): { sortBy: string[]; sortDesc: boolean[] } {
	const header: string | undefined = options && Array.isArray(options.sortBy) ? options.sortBy[0] : undefined;
	const desc: boolean = !!(options && Array.isArray(options.sortDesc) && options.sortDesc[0]);
	if (header === BOARDS_HEADER_VALUE.TITLE || header === BOARDS_HEADER_VALUE.DESCRIPTION) {
		return { sortBy: [header], sortDesc: [desc] };
	}
	if (header && header.startsWith(BOARDS_CUSTOM_FIELD_HEADER_PREFIX)) {
		return { sortBy: [header], sortDesc: [desc] };
	}
	return { sortBy: ['created'], sortDesc: [header === BOARDS_HEADER_VALUE.CREATED ? desc : true] };
}

/**
 * What the snackbar says about a failed fetch. A domain lockdown gets the
 * server's own words (which workspace to switch to); everything else gets the
 * page's line for its reason.
 */
export function boardsFetchFailureMessage(result: BoardsFetchResult, messageByReason: Record<string, string>): string {
	if (result.ok === true) return '';
	const failure: Exclude<BoardsFetchResult, { ok: true }> = result;
	if (failure.code === 'DOMAIN_LOCKED' && failure.message) return failure.message;
	return messageByReason[failure.reason] || messageByReason.failed;
}

/** Whether the list on screen is narrowed, so an empty one reads "no match" rather than "no boards". */
export function boardsListIsNarrowed(searchQuery: string | null | undefined, filters: BoardsPanelFilters | null | undefined): boolean {
	return !!((searchQuery || '').trim() || countPanelFilters(BOARDS_RESTING_FILTERS, filters));
}

/**
 * Whether the caller may delete a board: its owner, or a holder of
 * MANAGE_ALL_GENERATIONS (RunDiffusion admins hold every permission). The rule a
 * board's own Delete follows, and the one Firestore's rules hold every write to.
 */
export function canDeleteBoard(
	ownerUserId: string | null | undefined,
	userId: string | null | undefined,
	hasManageAllGenerations: boolean,
): boolean {
	return hasManageAllGenerations || (!!ownerUserId && ownerUserId === userId);
}

function boardsCount(count: number): string {
	return `${count} ${count === 1 ? 'board' : 'boards'}`;
}

/**
 * The snackbar after a bulk delete of `requested` boards, `deleted` of which are
 * gone (a board deleted elsewhere before this got to it counts as gone).
 */
export function bulkDeleteNotice(requested: number, deleted: number): { ok: boolean; message: string } {
	if (deleted >= requested) return { ok: true, message: `Success! Deleted ${boardsCount(deleted)}` };
	if (!deleted) return { ok: false, message: `Error! Problem deleting the ${requested === 1 ? 'board' : 'boards'}, please try again.` };
	return {
		ok: false,
		message: `Deleted ${deleted} of ${boardsCount(requested)}. ${requested - deleted} could not be deleted, please try again.`,
	};
}

/**
 * A board just created on this page, as the Boards list has its rows, to pin
 * there until the listing has it (utils/justAdded.ts). Its row binds the board
 * itself, so the avatar, owner and anything else this does not carry arrive live.
 */
export function justAddedBoardRow(runnit: Pick<Runnit, 'id' | 'title' | 'description' | 'accessLevel' | 'userId' | 'teamId' | 'customFields'>, createdIso: string): BoardRow {
	const customFields: Record<string, string> = (runnit.customFields || {}) as Record<string, string>;
	return {
		id: runnit.id,
		created: createdIso,
		title: runnit.title || null,
		description: runnit.description || null,
		accessLevel: runnit.accessLevel || RUNNITS_ACCESS_LEVEL.PRIVATE,
		ownerUserId: runnit.userId || null,
		ownerEmail: null,
		teamId: runnit.teamId || null,
		avatarUrl: null,
		customFields: Object.keys(customFields).map((key: string) => ({ key, label: null, value: customFields[key] || null })),
	};
}
